import { apiClient } from '@api/apiClient';
import {
  mapStopFacilityDetails,
  normalizeFacilityDetails,
} from '@common/facilityDetails';
import { V4ApiResponse } from '@api/types';
import { ToastType } from '@components/Toast/Toast';
import { IOrderViewModel } from '@typeDefinitions/orderTypes';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { logError } from '@utils/errorLogging';
import { handleToast } from '@utils/utils';
import { OrderTemplate } from '@store/orders/types';

import { buildSourceTrackingConfig } from './serviceHelpers';

export async function fetchOrderTemplates() {
  try {
    const response: V4ApiResponse<OrderTemplate[]> =
      await apiClient.get('Order/GetTemplates');
    if (!response.isSuccess) {
      handleToast(
        ToastType.Error,
        response.errors[0]?.message ?? 'Failed to get order templates',
      );
      return [];
    }
    return response.value;
  } catch (error: unknown) {
    logError('fetchOrderTemplates', error as Error);
    handleToast(ToastType.Error, 'Failed to get order templates');
  }
}

export async function deleteOrderTemplateService(
  id: number,
  changeTrackingSource?: ChangeTrackingSource,
) {
  try {
    const trackingConfig = buildSourceTrackingConfig(changeTrackingSource);

    const resp: V4ApiResponse<boolean | null> = await apiClient.post(
      `Order/DeleteTemplate/${id}`,
      undefined,
      trackingConfig,
    );
    const success = resp.isSuccess && resp.value !== false;
    if (success) {
      handleToast(ToastType.Success, 'Order template deleted');
    }
    return success;
  } catch (error: unknown) {
    logError('deleteOrderTemplateService', error as Error);
    handleToast(ToastType.Error, 'Failed to delete order template');
  }
}

export async function saveOrderTemplateCall(
  templateName: string,
  isPublic: boolean,
  template: Partial<IOrderViewModel>,
  id?: number,
  cb?: () => void,
  changeTrackingSource?: ChangeTrackingSource,
) {
  try {
    const trackingConfig = buildSourceTrackingConfig(changeTrackingSource);

    const resp: V4ApiResponse<boolean | null> = await apiClient.post(
      'Order/SaveTemplate',
      {
        name: templateName,
        public: isPublic,
        template: template,
        templateId: id ?? 0,
      },
      trackingConfig,
    );
    const success = resp.isSuccess && resp.value !== false;
    if (success) {
      handleToast(ToastType.Success, 'Order template saved');
    }
    return success;
  } catch (error: unknown) {
    logError('saveOrderTemplateCall', error as Error);
    handleToast(ToastType.Error, 'Failed to save order template');
    return false;
  } finally {
    cb && cb();
  }
}

export async function fetchOrderTemplateCall(templateId: number) {
  try {
    const resp: { value: IOrderViewModel } = await apiClient.get(
      `Order/LoadTemplate/${templateId}`,
    );
    return mapStopFacilityDetails(resp.value, normalizeFacilityDetails);
  } catch (error: unknown) {
    logError('fetchOrderTemplateCall', error as Error);
    handleToast(
      ToastType.Error,
      `Failed to fetc order template data for templateId: ${templateId}`,
    );
    throw new Error(
      'Failed to fetc order template data for templateId: ${templateId}',
    );
  }
}
